import { AxiosResponse } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';
import { HandleTableChange, ITableParams, RootStore } from '../../../stores';
import { INewsItem, IUserData, UserRoleEnum } from '../../user';
import apiRoutes from '../../../config/api-routes';
import { IBalanceTransferParams, ICreateNewsForm, IFee } from './console.types';
import { handleCommonTableChange, initTableParams } from '../../../utils/handleCommonTableChange';

class ConsoleStore {
  public rootStore: RootStore;
  public tableParams: ITableParams;
  public users: IUserData[] = [];
  public totalUsers = 0;
  public isShowModal = false;
  public selectedUser: IUserData | undefined;
  public news: INewsItem[] = [];
  public totalNews = 0;
  public isShowRolesModal = false;
  public transactionsPandingID = '';
  public fee: IFee | undefined;
  private userSearch = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.tableParams = initTableParams;
    makeAutoObservable(this);
  }

  public get isVerivield() {
    return 'this.data?.roles?.includes(UserRoleEnum.VERIFIED)';
  }

  public async getUsers() {
    const params: any = this.tableParams;

    if (this.userSearch) {
      params.search = this.userSearch;
      params.page = 1;
    }

    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.dashboardUsersAll,
      params: params,
      success: (response: AxiosResponse) => {
        console.log(response.data);
        this.users = response.data.users.map((user: IUserData) => ({ ...user, key: user.userId }));
        this.totalUsers = response.data.total;
      },
      errorMessage: 'Get users data failed',
    });
  }

  public updateRolesUser = async () => {
    const user = this.selectedUser;
    await this.rootStore.request({
      method: 'put',
      route: apiRoutes.dashboardUser,
      params: { userId: user?.userId, roles: user?.roles },
      success: async () => {
        await this.getUsers();
        this.closeRolesModal();
      },
      errorMessage: 'Update user failed',
    });
  };

  public sendTransaction = async (e: any) => {
    const params: IBalanceTransferParams = { userId: this.selectedUser?.userId };

    if (this.transactionsPandingID) {
      params.transactionId = this.transactionsPandingID;
    } else {
      params.amount = e.amount;
      params.typeTransaction = e.typeTransaction;
    }

    await this.rootStore.request({
      method: 'post',
      route: apiRoutes.transactionsBalanceTransfer,
      params,
      succesCode: 201,
      success: async () => {
        await this.getUsers();
        this.setTransactionsPandingID('');
        this.closeTransferModal();
      },
      errorMessage: 'Create transaction failed',
    });
  };

  public createNews = async (values: ICreateNewsForm) => {
    await this.rootStore.request({
      method: 'post',
      route: apiRoutes.news,
      params: values,
      succesCode: 201,
      success: async () => {
        await this.getAllNews();
      },
      errorMessage: 'Create transaction failed',
    });
  };

  public async getAllNews() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.newsAll,
      params: this.tableParams,
      success: (response: AxiosResponse) => {
        console.log(response.data);
        this.news = response.data.news.map((n: INewsItem) => ({ ...n, key: n.newsId }));
        this.totalNews = response.data.total;
      },
      errorMessage: 'Get news failed',
    });
  }

  public async getTotalFee() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.dashboardTotalFee,
      params: {},
      success: (response: AxiosResponse) => {
        this.fee = response.data;
      },
      errorMessage: 'Get total fee failed',
    });
  }

  public onSearch = (search: string) => {
    this.userSearch = search;
    this.getUsers();
  };

  handleTableChange: HandleTableChange<IUserData> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.tableParams, () => this.getUsers());
  };

  handleTableChangeNews: HandleTableChange<INewsItem> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.tableParams, () => this.getAllNews());
  };

  public showTransferModal = (user: IUserData) => {
    this.selectedUser = user;
    this.isShowModal = true;
  };

  public closeTransferModal = () => {
    this.rootStore.dispose();
    this.isShowModal = false;
  };

  public openRolesModal = (user: IUserData) => {
    runInAction(() => {
      this.selectedUser = user;
    });

    this.isShowRolesModal = true;
  };

  public closeRolesModal = () => {
    this.selectedUser = undefined;
    this.isShowRolesModal = false;
  };

  public addUserRole(role: UserRoleEnum) {
    if (this.selectedUser?.roles?.includes(role)) {
      return (this.selectedUser.roles = this.selectedUser?.roles?.filter(r => r !== role));
    }
    return this.selectedUser?.roles?.push(role);
  }

  public setMenu = (e: any) => {
    this.rootStore.navigation.goToPage(e.key);
  };

  public setTransactionsPandingID(id: string) {
    this.transactionsPandingID = id;
  }
}

export default ConsoleStore;
